import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Input } from '../../../../../common/ui';
import styles from '../styles.module.css';

const SOURCE_OPTIONS = [
  { value: 'supplier_invoice', label: 'Supplier Invoice' },
  { value: 'stock', label: 'Add Stock' },
  { value: 'none', label: 'No source' },
];

const round2 = (value) => Math.round(Number(value) * 100) / 100;

const isFiniteNumber = (value) => {
  if (value === '' || value === null || value === undefined) return false;
  return Number.isFinite(Number(value));
};

function invoiceNetUnitCost(row) {
  if (!row) return null;
  if (isFiniteNumber(row.unitCost) && Number(row.unitCost) >= 0) return round2(row.unitCost);
  if (row.discountType !== 'PER_UNIT' || !isFiniteNumber(row.unitPrice)) return null;
  const unitPrice = Number(row.unitPrice);
  const discountValue = isFiniteNumber(row.discountValue) ? Number(row.discountValue) : 0;
  let base;
  if (row.discountUnit === 'Rs') base = unitPrice - discountValue;
  else if (row.discountUnit === '%') base = unitPrice - round2((unitPrice * discountValue) / 100);
  else return null;
  const paidQty = Number(row.qty ?? row.purchasedQty) || 0;
  const freeQty = Number(row.freeQty) || 0;
  let net = base;
  if (freeQty > 0 && paidQty > 0) net = (base * paidQty) / (paidQty + freeQty);
  if (!Number.isFinite(net) || net < 0) return null;
  return round2(net);
}

function lotNetUnitCost(row) {
  if (!row || !isFiniteNumber(row.unit_cost) || Number(row.unit_cost) < 0) return null;
  return round2(row.unit_cost);
}

function lineIdentity(line) {
  if (line.sourceType === 'supplier_invoice') return `invoice:${line.sourceLineId}`;
  if (line.sourceType === 'stock') return `lot:${line.sourceId}`;
  return `product:${line.productId}`;
}

const AddLinePopup = ({
  supplierId,
  existingLines,
  onClose,
  onAdd,
  loadReturnSources,
  searchReturnProducts,
}) => {
  const [sourceKind, setSourceKind] = useState('supplier_invoice');
  const [sources, setSources] = useState({ invoices: [], lots: [] });
  const [invoiceId, setInvoiceId] = useState('');
  const [lineId, setLineId] = useState('');
  const [lotId, setLotId] = useState('');
  const [keyword, setKeyword] = useState('');
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState('');
  const [typedCost, setTypedCost] = useState('');
  const [error, setError] = useState('');
  const addingRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await loadReturnSources(supplierId);
      if (cancelled || !data) return;
      setSources({
        invoices: data.invoices || [],
        lots: data.lots || [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [supplierId, loadReturnSources]);

  useEffect(() => {
    if (sourceKind !== 'none') return undefined;
    const text = keyword.trim();
    if (!text) {
      setProducts([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const rows = await searchReturnProducts(text);
      if (!cancelled) setProducts(rows || []);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [keyword, sourceKind, searchReturnProducts]);

  const invoice = (sources.invoices || []).find((row) => String(row.id) === String(invoiceId));
  const invoiceLine = invoice && (invoice.lines || []).find((row) => String(row.id) === String(lineId));
  const lot = (sources.lots || []).find((row) => String(row.id) === String(lotId));
  const knownCost = useMemo(() => {
    if (sourceKind === 'supplier_invoice') return invoiceNetUnitCost(invoiceLine);
    if (sourceKind === 'stock') return lotNetUnitCost(lot);
    return null;
  }, [sourceKind, invoiceLine, lot]);

  const needsTypedCost = sourceKind === 'none' || (sourceKind !== 'none' && knownCost == null && (invoiceLine || lot));

  const addLine = () => {
    if (addingRef.current) return;
    const quantity = Number(qty);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError('Enter a quantity.');
      return;
    }
    let next = null;
    if (sourceKind === 'supplier_invoice') {
      if (!invoiceLine) {
        setError('That source is not for this supplier.');
        return;
      }
      const cost = knownCost == null ? Number(typedCost) : knownCost;
      if (knownCost == null && (!Number.isFinite(cost) || cost < 0)) {
        setError('Enter Net Unit Cost.');
        return;
      }
      next = {
        productId: invoiceLine.productId,
        productName: invoiceLine.productName,
        sourceType: 'supplier_invoice',
        sourceId: invoice.id,
        sourceLineId: invoiceLine.id,
        qty: quantity,
        netUnitCost: round2(cost),
      };
    } else if (sourceKind === 'stock') {
      if (!lot) {
        setError('That source is not for this supplier.');
        return;
      }
      const cost = knownCost == null ? Number(typedCost) : knownCost;
      if (knownCost == null && (!Number.isFinite(cost) || cost < 0)) {
        setError('Enter Net Unit Cost.');
        return;
      }
      next = {
        productId: lot.productId,
        productName: lot.productName,
        sourceType: 'stock',
        sourceId: lot.id,
        sourceLineId: null,
        qty: quantity,
        netUnitCost: round2(cost),
      };
    } else {
      const cost = Number(typedCost);
      if (!product || !Number.isFinite(cost) || cost < 0) {
        setError('Enter Net Unit Cost. No source was identified.');
        return;
      }
      next = {
        productId: product.id,
        productName: product.name,
        sourceType: null,
        sourceId: null,
        sourceLineId: null,
        qty: quantity,
        netUnitCost: round2(cost),
      };
    }
    next.returnValue = round2(next.qty * next.netUnitCost);
    next.id = `local-${Date.now()}`;
    if ((existingLines || []).some((line) => lineIdentity(line) === lineIdentity(next))) {
      setError('That source is already on this return.');
      return;
    }
    addingRef.current = true;
    onAdd(next);
    onClose();
  };

  return (
    <div className={styles.popupOverlay} role="dialog" aria-modal="true" aria-label="Add line">
      <div className={styles.popup}>
        <h2 className={styles.popupTitle}>Add line</h2>
        <Dropdown
          label="Source"
          options={SOURCE_OPTIONS}
          value={sourceKind}
          onChange={setSourceKind}
        />
        {sourceKind === 'supplier_invoice' && (
          <>
            <Dropdown
              label="Invoice"
              placeholder="Invoice"
              options={(sources.invoices || []).map((row) => ({
                value: row.id,
                label: row.invoiceNo || String(row.id),
              }))}
              value={invoiceId}
              onChange={(value) => {
                setInvoiceId(value);
                setLineId('');
              }}
            />
            <Dropdown
              label="Line"
              placeholder="Line"
              options={((invoice && invoice.lines) || []).map((row) => ({
                value: row.id,
                label: `${row.productName || 'Product'} · ${row.purchasedQty}`,
              }))}
              value={lineId}
              onChange={setLineId}
            />
          </>
        )}
        {sourceKind === 'stock' && (
          <Dropdown
            label="Lot"
            placeholder="Lot"
            options={(sources.lots || []).map((row) => ({
              value: row.id,
              label: `${row.productName || 'Product'} · ${row.initial_qty}`,
            }))}
            value={lotId}
            onChange={setLotId}
          />
        )}
        {sourceKind === 'none' && (
          <>
            <Input
              label="Product"
              value={keyword}
              onChange={(value) => {
                setKeyword(value);
                setProduct(null);
              }}
              placeholder="Code, name, or barcode"
            />
            <div className={styles.searchList}>
              {products.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={product && String(product.id) === String(row.id) ? styles.searchItemActive : styles.searchItem}
                  onClick={() => setProduct(row)}
                >
                  {row.name}
                </button>
              ))}
            </div>
          </>
        )}
        <Input label="Quantity" value={qty} onChange={setQty} />
        {(sourceKind === 'none' || needsTypedCost) && (
          <Input label="Net Unit Cost" value={typedCost} onChange={setTypedCost} />
        )}
        {knownCost != null && sourceKind !== 'none' && (
          <p className={styles.fieldValue}>Net Unit Cost {knownCost}</p>
        )}
        {error && <p className={styles.formError}>{error}</p>}
        <div className={styles.actionRow}>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button onClick={addLine}>Add line</Button>
        </div>
      </div>
    </div>
  );
};

AddLinePopup.propTypes = {
  supplierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  existingLines: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  loadReturnSources: PropTypes.func.isRequired,
  searchReturnProducts: PropTypes.func.isRequired,
};

AddLinePopup.defaultProps = {
  existingLines: [],
};

export default AddLinePopup;
